import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Eye, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Table, Column } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDeleteModal } from '../../components/ui/ConfirmDeleteModal';
import { Toast } from '../../components/ui/Toast';
import { InvestorForm } from '../../components/forms/InvestorForm';
import { ProtectedAction } from '../../components/ProtectedAction';
import { api } from '../../services/api';
import { ACCESS_INVESTORS, usePermissions } from '../../hooks/usePermissions';
import { Investor, Property } from '../../types';

function mapApiToInvestor(investor: any): Investor {
  return {
    id: investor.id,
    name: investor.name,
    email: investor.email,
    additionalEmails:
      investor.additionalEmails ??
      (investor.additional_emails ?? []).map((r: { email: string }) => r.email),
    phone: investor.phone || '',
    status: investor.status || 'active',
    type: investor.type || 'fisica',
    totalInvested: investor.totalInvested || 0,
    propertiesCount: investor.propertiesCount || 0,
    curp: investor.curp,
    csf: investor.csf,
    certificadoFIEL: investor.certificadoFIEL,
    idOficial: investor.idOficial,
    kyc: investor.kyc,
    caratulaBancaria: investor.caratulaBancaria,
    curpRepLegal: investor.curpRepLegal,
    actaConstitutiva: investor.actaConstitutiva,
    documents: investor.documents || investor.investor_documents || [],
    createdAt: investor.createdAt || new Date().toISOString(),
  };
}

export const InvestorsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [propertyFilter, setPropertyFilter] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState<Investor | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [investorPendingDelete, setInvestorPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [loadingInvestorDetail, setLoadingInvestorDetail] = useState(false);

  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');

  const { data: propertiesRes } = useQuery({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
  });
  const properties = propertiesRes?.data ?? [];

  const { data: investorsResponse, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['investors', 'list', propertyFilter],
    queryFn: () => api.getInvestors({ limit: 1000, propertyId: propertyFilter !== 'all' ? propertyFilter : undefined }),
    staleTime: 60_000,
  });
  const investorsData = investorsResponse?.data ?? investorsResponse ?? [];
  const allInvestors = useMemo(
    () =>
      investorsData
        .map((investor: any) => mapApiToInvestor(investor))
        .sort((a: Investor, b: Investor) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [investorsData],
  );
  const error = queryError ? (queryError as Error)?.message ?? 'Error al cargar los datos' : null;

  const createInvestorMutation = useMutation({
    mutationFn: ({ data, files }: { data: any; files?: any }) => api.createInvestor(data, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investors'] });
      handleCloseModal();
      setToast({ message: 'Inversionista agregado exitosamente', type: 'success' });
    },
    onError: (err: Error) => {
      setToast({ message: err.message || 'Error al guardar el inversionista', type: 'error' });
    },
  });
  const updateInvestorMutation = useMutation({
    mutationFn: ({ id, data, files }: { id: string; data: any; files?: any }) => api.updateInvestor(id, data, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investors'] });
      handleCloseModal();
      setToast({ message: 'Inversionista actualizado exitosamente', type: 'success' });
    },
    onError: (err: Error) => {
      setToast({ message: err.message || 'Error al guardar el inversionista', type: 'error' });
    },
  });
  const deleteInvestorMutation = useMutation({
    mutationFn: (id: string) => api.deleteInvestor(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['investors'] }),
  });

  // Filtrar inversionistas localmente según los filtros y búsqueda
  const investors = useMemo(() => {
    let filtered = [...allInvestors];

    // Filtro de búsqueda (nombre o email)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (investor) =>
          investor.name.toLowerCase().includes(searchLower) ||
          investor.email.toLowerCase().includes(searchLower)
      );
    }

    // Filtro por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter((investor) => investor.status === statusFilter);
    }

    // Filtro por propiedad (necesitaríamos cargar las inversiones para esto, pero por ahora lo omitimos)
    // Si necesitas filtrar por propiedad, necesitarías tener esa información en el investor o hacer una llamada adicional
    // Por ahora, si propertyFilter no es 'all', no aplicamos este filtro localmente
    // porque necesitaríamos datos de inversiones que no están en el objeto Investor básico

    // Asegurar que el orden se mantenga del más nuevo al más antiguo
    return filtered.sort((a: Investor, b: Investor) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Orden descendente (más nuevo primero)
    });
  }, [allInvestors, searchTerm, statusFilter]);

  const handleOpenModal = async (investor?: Investor) => {
    setIsModalOpen(true);
    if (!investor) {
      setEditingInvestor(undefined);
      setLoadingInvestorDetail(false);
      return;
    }
    setLoadingInvestorDetail(true);
    setEditingInvestor(undefined);
    try {
      const full = await api.getInvestor(investor.id);
      setEditingInvestor(mapApiToInvestor(full));
    } catch (e) {
      setToast({
        message: e instanceof Error ? e.message : 'No se pudo cargar el inversionista',
        type: 'error',
      });
      setIsModalOpen(false);
    } finally {
      setLoadingInvestorDetail(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingInvestor(undefined);
    setLoadingInvestorDetail(false);
  };

  const handleSubmit = (
    investorData: Omit<Investor, 'id' | 'createdAt' | 'totalInvested' | 'propertiesCount'>,
    files?: {
      csf?: File | null;
      idOficial?: File | null;
      kyc?: File | null;
      caratulaBancaria?: File | null;
      actaConstitutiva?: File | null;
    }
  ) => {
    setIsSubmitting(true);
    if (editingInvestor) {
      updateInvestorMutation.mutate(
        { id: editingInvestor.id, data: investorData, files },
        { onSettled: () => setIsSubmitting(false) },
      );
    } else {
      createInvestorMutation.mutate(
        { data: investorData, files },
        { onSettled: () => setIsSubmitting(false) },
      );
    }
  };

  const handleRequestDeleteInvestor = (id: string) => {
    const inv = allInvestors.find((i: Investor) => i.id === id);
    setInvestorPendingDelete({ id, name: inv?.name || 'este inversionista' });
  };

  const handleConfirmDeleteInvestor = () => {
    if (!investorPendingDelete) return;
    const { id } = investorPendingDelete;
    deleteInvestorMutation.mutate(id, {
      onSuccess: () => {
        setInvestorPendingDelete(null);
        setToast({ message: 'Inversionista eliminado', type: 'success' });
      },
      onError: (err: Error) => {
        setToast({ message: err.message || 'Error al eliminar el inversionista', type: 'error' });
        setInvestorPendingDelete(null);
      },
    });
  };

  const columns: Column<Investor>[] = [
    { header: 'Nombre', accessor: 'name', sortable: true },
    { header: 'Email', accessor: 'email', sortable: true },
    { header: 'Teléfono', accessor: 'phone' },
    {
      header: 'Total Invertido',
      accessor: (row) => (canViewPrices ? `$${row.totalInvested.toLocaleString()}` : '—'),
      sortable: true,
    },
    {
      header: '# Propiedades',
      accessor: 'propertiesCount',
      sortable: true,
    },
    {
      header: 'Estado',
      accessor: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {row.status === 'active' ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <div className="flex items-center space-x-2">
          <ProtectedAction permission={[...ACCESS_INVESTORS]}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/inversionistas/${row.id}`);
              }}
              className="text-primary hover:text-primary/80 p-1"
              title="Ver detalles"
              aria-label="Ver detalles del inversionista"
            >
              <Eye className="w-4 h-4" />
            </button>
          </ProtectedAction>
          <ProtectedAction permission={['update_investors', 'manage_investors']}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(row);
              }}
              className="text-gray-600 hover:text-gray-800 p-1"
              title="Editar"
              aria-label="Editar inversionista"
            >
              <Edit className="w-4 h-4" />
            </button>
          </ProtectedAction>
          <ProtectedAction permission="delete_investors">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestDeleteInvestor(row.id);
              }}
              disabled={deleteInvestorMutation.isPending && investorPendingDelete?.id === row.id}
              className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Eliminar"
              aria-label="Eliminar inversionista"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </ProtectedAction>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Cargando inversionistas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Inversionistas</h1>
        <ProtectedAction permission={['create_investors', 'manage_investors']}>
          <Button
            icon={Plus}
            onClick={() => handleOpenModal()}
          >
            Agregar Inversionista
          </Button>
        </ProtectedAction>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todas las propiedades</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
              </option>
            ))}
          </select>
        </div>

        {investors.length === 0 && !loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' || propertyFilter !== 'all'
                ? 'No se encontraron inversionistas que coincidan con los filtros'
                : 'No hay inversionistas registrados'}
            </p>
          </div>
        ) : (
          <Table
            data={investors}
            columns={columns}
            onRowClick={(row) => navigate(`/inversionistas/${row.id}`)}
          />
        )}
      </div>

      {/* Modal para Agregar/Editar Inversionista */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingInvestor ? 'Editar Inversionista' : 'Agregar Nuevo Inversionista'}
        size="xl"
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loadingInvestorDetail ? (
          <div className="flex justify-center py-12 text-gray-500 text-sm">Cargando datos del inversionista…</div>
        ) : (
          <InvestorForm
            investor={editingInvestor}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isLoading={isSubmitting}
          />
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={investorPendingDelete !== null}
        onClose={() => {
          if (!deleteInvestorMutation.isPending) setInvestorPendingDelete(null);
        }}
        onConfirm={handleConfirmDeleteInvestor}
        message={
          investorPendingDelete ? (
            <>
              ¿Seguro que deseas eliminar al inversionista <strong>{investorPendingDelete.name}</strong>?
            </>
          ) : null
        }
        isLoading={deleteInvestorMutation.isPending}
      />

      {/* Toast de notificación */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

